import { useEffect, useRef } from 'react';
import Phaser from 'phaser';

import { createGameConfig } from '@/features/bouncySums/game/config';
import { EventBus, EVENTS } from '@/features/bouncySums/game/events/bouncySumsEventBus';
import { syncPhaserCanvasSize } from '@/shared/utils/phaserCanvasSize';
import { createPhaserGameLifecycle } from '@/shared/utils/phaserGameLifecycle';

import type { RefObject } from 'react';
import type { BouncySumsScreen } from '@/features/bouncySums/constants';
import type { BouncySumsConfig, BouncySumsQuestion } from '@/features/bouncySums/types';

type BouncySumsGamePayload = {
	config: BouncySumsConfig;
	questions: BouncySumsQuestion[];
};

type UseBouncySumsPhaserGameParams = {
	activeScreen: BouncySumsScreen;
	audioOn: boolean;
	containerRef: RefObject<HTMLDivElement | null>;
	gamePayload: BouncySumsGamePayload;
	startGameTimeoutRef: { current: number | null };
};

const gameLifecycle = createPhaserGameLifecycle();

export function useBouncySumsPhaserGame({
	activeScreen,
	audioOn,
	containerRef,
	gamePayload,
	startGameTimeoutRef,
}: UseBouncySumsPhaserGameParams): void {
	const gameRef = useRef<Phaser.Game | null>(null);
	const audioOnRef = useRef(audioOn);
	const sceneReadyRef = useRef(false);
	const gamePayloadRef = useRef(gamePayload);

	useEffect(() => {
		audioOnRef.current = audioOn;
		EventBus.emit(EVENTS.SET_AUDIO_ON, audioOn);
	}, [audioOn]);

	useEffect(() => {
		gamePayloadRef.current = gamePayload;

		if (sceneReadyRef.current && gamePayload.questions.length) {
			EventBus.emit(EVENTS.LOAD_GAME, {
				...gamePayload,
				audioOn: audioOnRef.current,
			});
		}
	}, [gamePayload]);

	useEffect(() => {
		let isMounted = true;
		let resizeObserver: ResizeObserver | null = null;
		let handleResize: (() => void) | null = null;

		const handleSceneReady = () => {
			sceneReadyRef.current = true;

			if (!gamePayloadRef.current.questions.length) {
				return;
			}

			EventBus.emit(EVENTS.LOAD_GAME, {
				...gamePayloadRef.current,
				audioOn: audioOnRef.current,
			});
		};

		const mountGame = async () => {
			await gameLifecycle.waitForPendingDestroy();

			const container = containerRef.current;
			if (!isMounted || !container || gameRef.current) {
				return;
			}

			const game = new Phaser.Game(createGameConfig(container));
			gameRef.current = game;
			syncPhaserCanvasSize(game, container, { fallback: 'viewport' });

			handleResize = () => {
				if (gameRef.current && container.isConnected) {
					syncPhaserCanvasSize(gameRef.current, container, { fallback: 'viewport' });
				}
			};

			if (typeof ResizeObserver !== 'undefined') {
				resizeObserver = new ResizeObserver(handleResize);
				resizeObserver.observe(container);
			} else {
				window.addEventListener('resize', handleResize);
			}
		};

		EventBus.on(EVENTS.SCENE_READY, handleSceneReady);
		mountGame();

		return () => {
			if (startGameTimeoutRef.current !== null) {
				window.clearTimeout(startGameTimeoutRef.current);
				startGameTimeoutRef.current = null;
			}

			isMounted = false;
			resizeObserver?.disconnect();
			if (handleResize) {
				window.removeEventListener('resize', handleResize);
			}
			EventBus.off(EVENTS.SCENE_READY, handleSceneReady);
			sceneReadyRef.current = false;

			if (gameRef.current) {
				gameLifecycle.destroy(gameRef.current);
			}

			gameRef.current = null;
		};
	}, [containerRef, startGameTimeoutRef]);

	useEffect(() => {
		const container = containerRef.current;
		const game = gameRef.current;

		if (!container || !game) {
			return;
		}

		window.requestAnimationFrame(() => {
			if (gameRef.current && container.isConnected) {
				syncPhaserCanvasSize(gameRef.current, container, { fallback: 'viewport' });
			}
		});
	}, [activeScreen, containerRef]);
}
